import React, { forwardRef, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import Button from '~/components/atom/Button';
import JustificationContainer from '~/components/atom/JustificationContainer';

const DEFAULT_MAX_WIDTH = 670;

export type Props = {
  dataTestId?: string;
  className?: string;
  /** Component/text to be displayed on the left side of the bar */
  information: React.ReactNode;
  action: {
    onClick: () => void;
    label: string;
  };
};

const ActionBar = forwardRef<HTMLDivElement, Props>(
  ({ dataTestId, className, information, action }, ref) => {
    const [offset, setOffset] = useState(0);
    const [maxWidth, setMaxWidth] = useState(DEFAULT_MAX_WIDTH);

    useEffect(() => {
      const updateDimensions = () => {
        if (ref && 'current' in ref && ref.current) {
          const rect = ref.current.getBoundingClientRect();
          setOffset(rect.width / 2);

          // Set ActionBar width to match ref width, but with some constraints
          const refWidth = rect.width;
          const actionBarWidth = Math.min(
            Math.max(refWidth, 300),
            DEFAULT_MAX_WIDTH,
          );
          setMaxWidth(actionBarWidth);
        }
      };

      updateDimensions();

      window.addEventListener('resize', updateDimensions);

      return () => window.removeEventListener('resize', updateDimensions);
    }, [ref]);

    return (
      <Container $offset={offset} $maxWidth={maxWidth}>
        <JustificationContainer
          dataTestId={dataTestId}
          className={className}
          justification="space-between"
          align="center"
          width="100%"
          backgroundColor={{ group: 'tertiary' }}
          padding={['base', 'l']}
          border={{ radius: 'm' }}
          boxShadow="around"
        >
          {information}
          <Button onClick={action.onClick} label={action.label} size="medium" />
        </JustificationContainer>
      </Container>
    );
  },
);

const Container = styled.div<{
  $offset: number;
  $maxWidth: number;
}>(
  ({ $offset, $maxWidth }) => css`
    position: fixed;
    bottom: 40px;
    transform: translateX(calc(-50% + ${$offset}px));
    width: 100%;
    max-width: ${$maxWidth}px;
    min-width: 300px;
    transition:
      bottom 0.2s ease-out,
      max-width 0.2s ease-out;

    // Prevent ActionBar from going too low
    @media (min-height: 1250px) {
      bottom: calc(100vh - 1250px);
    }
  `,
);

export default ActionBar;
